import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api } from "../../data";
import { useAction, useRueckfrage } from "../../components/ui";
import { date, time } from "../../lib/format";
import { Fehler, Laden } from "./parts";
import { OwnerSelect, RabattVorschlag, SchnellErfassen, StufePill, StufeWahl, useLead, useStaff, Verlauf } from "./crm-teile";
import { deleteLead, leadName, objectLead, QUELLEN, quelleLabel, setOwner, updateLead, type LeadInput, type LeadRow, type Quelle } from "./crm-data";

/**
 * Lead-Akte (Vertrieb-Konsole, Inhaber 02.11.2026): Kontakt, Herkunft, Stufe, Zuständig, nächster Schritt, Thema, Verlauf und
 * schnelles Erfassen auf einer Seite. Übergabe durch Ändern des Zuständigen; Rabatt nur als Vorschlag an den Admin; Widerspruch
 * löscht Kontaktdaten und Verlauf sofort.
 */
export function LeadAkte() {
  const { id = "" } = useParams();
  const q = useLead(id);
  const staff = useStaff();
  if (q.error) return <><title>Lead · Konsole · CreatorOffice</title><h1>Lead</h1><Fehler error={q.error} retry={() => void q.refetch()} /></>;
  if (q.isPending) return <><title>Lead · Konsole · CreatorOffice</title><h1>Lead</h1><Laden /></>;
  const l = q.data?.lead;
  if (!l) {
    return (
      <>
        <title>Lead · Konsole · CreatorOffice</title>
        <h1>Lead nicht gefunden</h1>
        <div className="panel stack tk-empty"><p>Diesen Lead gibt es nicht (mehr), etwa nach Ablauf der Löschfrist.</p><Link className="btn" to="/intern/leads">Zur Leadliste</Link></div>
      </>
    );
  }
  const gesperrt = !!l.objected_at;
  return (
    <>
      <title>{`${leadName(l)} · Lead · CreatorOffice`}</title>
      <div className="pagehead">
        <div>
          <div className="eyebrow"><Link to="/intern/leads">Leads</Link> · <Link to="/intern/pipeline">Pipeline</Link></div>
          <h1>{leadName(l)}</h1>
          <div className="row tk-crm-kopfzeile"><StufePill stufe={l.stage} />{l.stage === "verloren" && l.lost_reason && <small>Grund: {l.lost_reason}</small>}
            {l.org_id && <Link to={`/intern/agenturen/${l.org_id}`}>Kundenakte öffnen</Link>}</div>
        </div>
      </div>
      {gesperrt && <div className="notice" role="status">Widerspruch am {date(l.objected_at)}: Kontaktdaten und Verlauf sind gelöscht, die Adresse ist für die Automatik gesperrt. Hier wird nichts mehr erfasst.</div>}
      <div className="grid tk-crm-akte">
        <div className="stack">
          <Stand lead={l} gesperrt={gesperrt} />
          {!gesperrt && <Kontakt lead={l} />}
          <Herkunft lead={l} />
        </div>
        <div className="stack">
          {!gesperrt && (
            <section className="panel stack" aria-labelledby="lead-erfassen">
              <h2 id="lead-erfassen">Schnell erfassen</h2>
              <SchnellErfassen leadId={l.id} ownerId={l.owner_id} />
            </section>
          )}
          <section className="panel stack" aria-labelledby="lead-verlauf">
            <h2 id="lead-verlauf">Verlauf</h2>
            <Verlauf eintraege={q.data?.verlauf ?? []} staff={staff.data} />
          </section>
          <Weiteres lead={l} gesperrt={gesperrt} />
        </div>
      </div>
    </>
  );
}

function Stand({ lead: l, gesperrt }: { lead: LeadRow; gesperrt: boolean }) {
  const { busy, error, run } = useAction();
  const [schritt, setSchritt] = useState(l.next_step ?? "");
  const [am, setAm] = useState(l.next_step_at ?? "");
  const [thema, setThema] = useState(l.topic ?? "");
  const [tarif, setTarif] = useState(l.plan_interest ?? "");
  async function speichern(e: FormEvent) {
    e.preventDefault();
    await run("stand", () => updateLead(api, l.id, { next_step: schritt, next_step_at: am, topic: thema, plan_interest: tarif }), "Gespeichert.");
  }
  return (
    <section className="panel stack" aria-labelledby="lead-stand">
      <h2 id="lead-stand">Stand</h2>
      <div className="grid tk-crm-aufgabe">
        <StufeWahl leadId={l.id} stufe={l.stage} disabled={gesperrt} />
        <OwnerSelect value={l.owner_id} disabled={gesperrt || !!busy} onChange={(owner) => void run("owner", () => setOwner(api, l.id, owner), "Zuständig geändert. Offene Aufgaben gehen mit.")} />
      </div>
      {!gesperrt && (
        <form className="stack" onSubmit={(e) => void speichern(e)}>
          <div className="grid tk-crm-aufgabe">
            <label>Nächster Schritt<input maxLength={300} value={schritt} onChange={(e) => setSchritt(e.target.value)} placeholder="z. B. Demo anbieten" /></label>
            <label>Am<input type="date" value={am} onChange={(e) => setAm(e.target.value)} /></label>
          </div>
          <label>Aktuelles Thema<input maxLength={300} value={thema} onChange={(e) => setThema(e.target.value)} placeholder="Worum geht es gerade?" /></label>
          <label>Tarif-Interesse<input maxLength={60} value={tarif} onChange={(e) => setTarif(e.target.value)} placeholder="z. B. Agentur, Enterprise" /></label>
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row"><button type="submit" className="primary" disabled={!!busy} aria-busy={busy === "stand"}>Speichern</button></div>
        </form>
      )}
    </section>
  );
}

function Kontakt({ lead: l }: { lead: LeadRow }) {
  const { busy, error, run } = useAction();
  const [e, setE] = useState<LeadInput>({ company: l.company ?? "", contact_name: l.contact_name ?? "", email: l.email ?? "", phone: l.phone ?? "",
    website: l.website ?? "", country: l.country ?? "", creators: l.creators ?? "" });
  const feld = (k: keyof LeadInput) => ({ value: (e[k] as string) ?? "", onChange: (x: { target: { value: string } }) => setE({ ...e, [k]: x.target.value }) });
  return (
    <section className="panel stack" aria-labelledby="lead-kontakt">
      <h2 id="lead-kontakt">Kontakt</h2>
      <form className="stack" onSubmit={(ev) => { ev.preventDefault(); void run("kontakt", () => updateLead(api, l.id, e), "Kontakt gespeichert."); }}>
        <div className="grid tk-crm-formular">
          <label>Firma<input maxLength={160} {...feld("company")} /></label>
          <label>Kontaktperson<input maxLength={120} {...feld("contact_name")} /></label>
          <label>E-Mail<input type="email" maxLength={200} {...feld("email")} /></label>
          <label>Telefon<input type="tel" maxLength={40} {...feld("phone")} /></label>
          <label>Website oder Instagram<input maxLength={200} {...feld("website")} /></label>
          <label>Land<input maxLength={2} {...feld("country")} /></label>
          <label>Anzahl Creator<input maxLength={30} {...feld("creators")} /></label>
        </div>
        <div className="row tk-crm-direkt">
          {l.email && <a className="btn" href={`mailto:${l.email}`}>Mail schreiben</a>}
          {l.phone && <a className="btn" href={`tel:${l.phone.replace(/[^\d+]/g, "")}`}>Anrufen</a>}
        </div>
        {error && <p className="tk-error" role="alert">{error}</p>}
        <div className="row"><button type="submit" className="primary" disabled={!!busy} aria-busy={busy === "kontakt"}>Kontakt speichern</button></div>
      </form>
    </section>
  );
}

function Herkunft({ lead: l }: { lead: LeadRow }) {
  const { busy, run } = useAction();
  const [quelle, setQuelle] = useState<Quelle>(l.source);
  return (
    <section className="panel stack" aria-labelledby="lead-herkunft">
      <h2 id="lead-herkunft">Herkunft</h2>
      <dl className="tk-crm-fakten">
        <div><dt>Quelle</dt><dd>{quelleLabel(l.source)}</dd></div>
        {l.source_detail && <div><dt>Detail</dt><dd>{l.source_detail}</dd></div>}
        {l.demo_request_id && <div><dt>Demo-Anfrage</dt><dd><Link to="/intern/demo-termine">bei den Demo-Anfragen</Link></dd></div>}
        {l.enterprise_request_id && <div><dt>Enterprise-Anfrage</dt><dd>über das Formular der Website</dd></div>}
        <div><dt>Konto</dt><dd>{l.org_id ? <Link to={`/intern/agenturen/${l.org_id}`}>Kundenakte</Link> : "noch keins"}</dd></div>
        <div><dt>Angelegt</dt><dd>{date(l.created_at)} {time(l.created_at)}</dd></div>
        <div><dt>Letzter Kontakt</dt><dd>{l.last_contact_at ? `${date(l.last_contact_at)} ${time(l.last_contact_at)}` : "noch keiner"}</dd></div>
        <div><dt>Stufe seit</dt><dd>{date(l.stage_changed_at)}</dd></div>
      </dl>
      {!l.objected_at && (
        <label>Quelle ändern
          <select value={quelle} disabled={!!busy} onChange={(e) => { const v = e.target.value as Quelle; setQuelle(v); void run("quelle", () => updateLead(api, l.id, { source: v }), "Quelle geändert."); }}>
            {QUELLEN.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
          </select>
        </label>
      )}
    </section>
  );
}

function Weiteres({ lead: l, gesperrt }: { lead: LeadRow; gesperrt: boolean }) {
  const { busy, run } = useAction();
  const frage = useRueckfrage();
  const navigate = useNavigate();
  if (gesperrt && l.org_id) return null;
  return (
    <section className="panel stack" aria-labelledby="lead-weiteres">
      <h2 id="lead-weiteres">Weitere Aktionen</h2>
      <div className="row tk-crm-aktionen">
        {!gesperrt && <RabattVorschlag leadId={l.id} />}
        {!gesperrt && (
          <button type="button" disabled={!!busy} onClick={() => void (async () => {
            if (await frage.ask({ title: "Widerspruch vermerken?", danger: true, confirmLabel: "Widerspruch vermerken",
              text: "Die Person hat der Verarbeitung im Vertrieb widersprochen.\nKontaktdaten und Verlauf werden sofort gelöscht; die Adresse wird nur als Prüfsumme gesperrt, damit die Automatik sie nicht wieder anlegt. Das lässt sich nicht rückgängig machen." })) {
              await run("widerspruch", () => objectLead(api, l.id), "Widerspruch vermerkt, Kontaktdaten gelöscht.");
            }
          })()}>Widerspruch vermerken</button>
        )}
        {!l.org_id && (
          <button type="button" className="danger" disabled={!!busy} onClick={() => void (async () => {
            if (await frage.ask({ title: "Lead löschen?", danger: true, confirmLabel: "Löschen", text: "Für Dubletten und Irrläufer. Lead und Verlauf werden endgültig gelöscht." })) {
              const ok = await run("loeschen", () => deleteLead(api, l.id).then(() => true), "Lead gelöscht.");
              if (ok) navigate("/intern/leads");
            }
          })()}>Lead löschen</button>
        )}
      </div>
      <small className="muted">Rabatte legt nur ein Admin an; der Vorschlag landet auf seiner Startseite. Leads mit Konto bleiben an der Kundenakte.</small>
      {frage.element}
    </section>
  );
}
